/**
 * Minimize a Repancy desktop window.
 *
 * Wraps WindowContext.minimizeWindow. The window state flips from
 * 'open' to 'minimized' but the window registry entry is preserved
 * (zIndex, position) for restoration via openWindow or focusWindow.
 */

import type { NipeTool } from './types';
import { NIPE_KNOWN_WINDOWS } from './openWindow';

export interface MinimizeWindowContext {
  minimizeWindow?: (path: string) => Promise<{ minimized: boolean }>;
}

const KNOWN_PATHS = NIPE_KNOWN_WINDOWS.map((w) => w.path);

export const minimizeWindowTool: NipeTool<MinimizeWindowContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'minimizeWindow',
      description:
        'Minimize a Repancy desktop window without closing it. Use when ' +
        'the user wants to set a window aside but keep its state. Known ' +
        'paths: ' + KNOWN_PATHS.join(', '),
      parameters: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description: 'Window path. One of: ' + KNOWN_PATHS.join(', '),
          },
        },
        required: ['path'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.minimizeWindow) {
      return { error: 'minimizeWindow integration is not available in this session.' };
    }
    const path = typeof args.path === 'string' ? args.path : '';
    if (!KNOWN_PATHS.includes(path)) {
      return {
        error: `Unknown window path "${path}". Known: ${KNOWN_PATHS.join(', ')}`,
      };
    }
    return await ctx.minimizeWindow(path);
  },
};
